import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useTeamMembers from '../hooks/useTeamMembers';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../dateUtils';
import { MEETING_TYPES } from '../labels';
import type { Confidentiality, LeadershipMeeting, LeadershipMeetingInput, MeetingType } from '../types';

interface FormState {
  title: string;
  type: MeetingType;
  scheduled_at: string;
  location_or_link: string;
  confidentiality: Confidentiality;
  participant_user_ids: string[];
  chair_user_id: string;
  secretary_user_id: string;
}

const EMPTY_FORM: FormState = {
  title: '',
  type: 'ad_hoc',
  scheduled_at: '',
  location_or_link: '',
  confidentiality: 'normaal',
  participant_user_ids: [],
  chair_user_id: '',
  secretary_user_id: '',
};

function toFormState(meeting: LeadershipMeeting | null): FormState {
  if (!meeting) return EMPTY_FORM;
  return {
    title: meeting.title,
    type: meeting.type,
    scheduled_at: toDatetimeLocalValue(meeting.scheduled_at),
    location_or_link: meeting.location_or_link || '',
    confidentiality: meeting.confidentiality,
    participant_user_ids: meeting.participant_user_ids,
    chair_user_id: meeting.chair_user_id || '',
    secretary_user_id: meeting.secretary_user_id || '',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: LeadershipMeeting | null;
  onSubmit: (data: LeadershipMeetingInput) => Promise<void>;
}

export default function MeetingFormDialog({ open, onOpenChange, meeting, onSubmit }: Props) {
  const teamMembers = useTeamMembers();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(toFormState(meeting));
  }, [open, meeting]);

  const toggleParticipant = (userId: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      participant_user_ids: checked
        ? [...prev.participant_user_ids, userId]
        : prev.participant_user_ids.filter((id) => id !== userId),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title || !form.scheduled_at) return;
    setSubmitting(true);
    try {
      await onSubmit({
        title: form.title,
        type: form.type,
        scheduled_at: fromDatetimeLocalValue(form.scheduled_at),
        location_or_link: form.location_or_link || null,
        confidentiality: form.confidentiality,
        participant_user_ids: form.participant_user_ids,
        chair_user_id: form.chair_user_id || null,
        secretary_user_id: form.secretary_user_id || null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{meeting ? 'Vergadering bewerken' : 'Nieuwe vergadering'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="meeting-title">Titel</Label>
            <Input
              id="meeting-title"
              value={form.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meeting-type">Type</Label>
              <Select value={form.type} onValueChange={(value: MeetingType) => setForm({ ...form, type: value })}>
                <SelectTrigger id="meeting-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="meeting-scheduled-at">Datum/tijd</Label>
              <Input
                id="meeting-scheduled-at"
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, scheduled_at: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="meeting-location">Locatie / link</Label>
            <Input
              id="meeting-location"
              value={form.location_or_link}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, location_or_link: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meeting-chair">Voorzitter</Label>
              <Select
                value={form.chair_user_id || undefined}
                onValueChange={(value: string) => setForm({ ...form, chair_user_id: value })}
              >
                <SelectTrigger id="meeting-chair"><SelectValue placeholder="Kies iemand" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="meeting-secretary">Notulist</Label>
              <Select
                value={form.secretary_user_id || undefined}
                onValueChange={(value: string) => setForm({ ...form, secretary_user_id: value })}
              >
                <SelectTrigger id="meeting-secretary"><SelectValue placeholder="Kies iemand" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Deelnemers</Label>
            <div className="mt-2 max-h-40 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
              {teamMembers.map((member) => (
                <label key={member.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.participant_user_ids.includes(member.id)}
                    onCheckedChange={(checked: boolean) => toggleParticipant(member.id, checked === true)}
                  />
                  {member.name}
                </label>
              ))}
              {teamMembers.length === 0 && <p className="text-sm text-slate-400">Geen teamleden gevonden.</p>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="meeting-confidential"
              checked={form.confidentiality === 'vertrouwelijk'}
              onCheckedChange={(checked: boolean) =>
                setForm({ ...form, confidentiality: checked ? 'vertrouwelijk' : 'normaal' })
              }
            />
            <Label htmlFor="meeting-confidential" className="font-normal">
              Vertrouwelijk -- alleen zichtbaar voor deelnemers en admin
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting}>{meeting ? 'Opslaan' : 'Aanmaken'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
